import { tracked } from "@glimmer/tracking";
import Controller from "@ember/controller";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import { eventDetailUrl } from "../lib/dec-event-url";
import {
  buildHoursFields,
  DAY_OPTIONS,
  publicationLabel as resourcePublicationLabel,
  serializeHoursFields,
  updateHoursField,
} from "../lib/dec-hours";
import { uploadFromResource, uploadImageFile, uploadPreviewUrl } from "../lib/dec-image-upload";

function extractErrorMessage(error) {
  const responseJSON = error?.jqXHR?.responseJSON || error?.responseJSON;
  const errors = responseJSON?.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    return errors.join(" ");
  }

  if (typeof errors === "string") {
    return errors;
  }

  if (responseJSON?.error) {
    return responseJSON.error;
  }

  if (responseJSON?.message) {
    return responseJSON.message;
  }

  return "Room could not be updated. Check the room details and try again.";
}

function pad(value) {
  return `${value}`.padStart(2, "0");
}

// See safeDateFormat in discourse-events-calendar.js — kept identical
// across controllers.
function safeDateFormat(date, options, fallback) {
  if (!date) {
    return fallback;
  }
  try {
    const dateObject = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(dateObject.getTime())) {
      return fallback;
    }
    return new Intl.DateTimeFormat(undefined, options).format(dateObject);
  } catch {
    return fallback;
  }
}

function fallbackDate(date) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function fallbackWeekday(date) {
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][date.getDay()];
}

function fallbackTime(date) {
  let hours = date.getHours();
  const minutes = pad(date.getMinutes());
  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${hours}:${minutes} ${period}`;
}

function eventDate(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return safeDateFormat(
    date,
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    },
    fallbackDate(date)
  );
}

function eventTime(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return safeDateFormat(
    date,
    {
      hour: "numeric",
      minute: "2-digit",
    },
    fallbackTime(date)
  );
}

function timeLabel(value) {
  const [hours, minutes] = `${value || ""}`.split(":").map((part) => parseInt(part, 10));

  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
    return value || "";
  }

  return eventTime(new Date(2000, 0, 1, hours, minutes).toISOString());
}

function minutesFromTime(value) {
  const [hours, minutes] = `${value || ""}`.split(":").map((part) => parseInt(part, 10));

  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
    return null;
  }

  return hours * 60 + minutes;
}

function hasHoursPolicy(hours) {
  return Object.values(hours || {}).some((intervals) => Array.isArray(intervals) && intervals.length > 0);
}

function intervalsForDay(hours, dayKey) {
  return (hours?.[dayKey] || [])
    .map((interval) => ({
      start: interval?.start,
      end: interval?.end,
      startMinutes: minutesFromTime(interval?.start),
      endMinutes: minutesFromTime(interval?.end),
    }))
    .filter(
      (interval) =>
        Number.isInteger(interval.startMinutes) &&
        Number.isInteger(interval.endMinutes) &&
        interval.endMinutes > interval.startMinutes
    )
    .sort((left, right) => left.startMinutes - right.startMinutes);
}

function intersectIntervals(leftIntervals, rightIntervals) {
  const intersections = [];

  leftIntervals.forEach((left) => {
    rightIntervals.forEach((right) => {
      const startMinutes = Math.max(left.startMinutes, right.startMinutes);
      const endMinutes = Math.min(left.endMinutes, right.endMinutes);

      if (endMinutes > startMinutes) {
        intersections.push({
          start: `${pad(Math.floor(startMinutes / 60))}:${pad(startMinutes % 60)}`,
          end: `${pad(Math.floor(endMinutes / 60))}:${pad(endMinutes % 60)}`,
          startMinutes,
          endMinutes,
        });
      }
    });
  });

  return intersections;
}

function intervalRangeLabel(interval) {
  return `${timeLabel(interval.start)} - ${timeLabel(interval.end)}`;
}

function effectiveHoursForDate(availability, date) {
  const dayKey = DAY_OPTIONS[date.getDay()]?.key;
  const roomHours = availability?.hours_of_operation || {};
  const locationHours = availability?.location_hours_of_operation || {};
  const roomHasHours = hasHoursPolicy(roomHours);
  const locationHasHours = hasHoursPolicy(locationHours);

  if (!dayKey || (!roomHasHours && !locationHasHours)) {
    return {
      isBookable: true,
      label: "No hours restrictions",
    };
  }

  const roomIntervals = intervalsForDay(roomHours, dayKey);
  const locationIntervals = intervalsForDay(locationHours, dayKey);

  if (locationHasHours && locationIntervals.length === 0) {
    return {
      isBookable: false,
      label: "Closed by location hours",
    };
  }

  if (roomHasHours && roomIntervals.length === 0) {
    return {
      isBookable: false,
      label: "Closed by room hours",
    };
  }

  let effectiveIntervals = [];

  if (roomHasHours && locationHasHours) {
    effectiveIntervals = intersectIntervals(roomIntervals, locationIntervals);
  } else {
    effectiveIntervals = roomHasHours ? roomIntervals : locationIntervals;
  }

  if (effectiveIntervals.length === 0) {
    return {
      isBookable: false,
      label: "No overlapping room/location hours",
    };
  }

  return {
    isBookable: true,
    label: effectiveIntervals.map(intervalRangeLabel).join(", "),
  };
}

function sameLocalDate(left, right) {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function eventDateTime(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return `${eventDate(value)} at ${eventTime(value)}`;
}

function eventTimeRange(startsAtValue, endsAtValue, allDay = false) {
  if (allDay) {
    return "All day";
  }

  if (!startsAtValue) {
    return "";
  }

  if (!endsAtValue) {
    return eventTime(startsAtValue);
  }

  const startsAt = new Date(startsAtValue);
  const endsAt = new Date(endsAtValue);

  if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) {
    return "";
  }

  if (sameLocalDate(startsAt, endsAt)) {
    return `${eventTime(startsAtValue)} - ${eventTime(endsAtValue)}`;
  }

  return `${eventDateTime(startsAtValue)} - ${eventDateTime(endsAtValue)}`;
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date, days) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function overlapsDay(item, dayStart, dayEnd) {
  const startsAt = new Date(item.starts_at);
  const endsAt = new Date(item.ends_at);

  return (
    !Number.isNaN(startsAt.getTime()) &&
    !Number.isNaN(endsAt.getTime()) &&
    startsAt < dayEnd &&
    endsAt > dayStart
  );
}

function sortEvents(events) {
  return [...events].sort((left, right) => {
    const leftTime = new Date(left.starts_at).getTime() || 0;
    const rightTime = new Date(right.starts_at).getTime() || 0;
    return leftTime - rightTime;
  });
}

function locationUrl(locationRecord) {
  const value = locationRecord?.slug || locationRecord?.id || locationRecord?.name || "";
  return value ? `/calendar/locations/${encodeURIComponent(value)}` : "";
}

function locationValue(location) {
  return `${location?.id || location?.slug || location?.name || ""}`;
}

function normalizeGroupIds(value) {
  const items = Array.isArray(value) ? value : `${value || ""}`.split(/[|,]/);

  return items
    .map((item) => (typeof item === "object" ? item?.id || item?.value : item))
    .map((item) => Number.parseInt(item, 10))
    .filter((item) => Number.isInteger(item) && item > 0);
}

function groupLabel(ids, groupsById) {
  return normalizeGroupIds(ids)
    .map((id) => groupsById.get(id) || `#${id}`)
    .join(", ");
}

function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function timezoneOptions(selectedTimezone, blankLabel) {
  const selected = `${selectedTimezone || ""}`.trim();
  const supportedZones =
    typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  const zones = [
    selected,
    browserTimeZone(),
    "UTC",
    "America/Los_Angeles",
    "America/New_York",
    "Europe/London",
    "Europe/Berlin",
    "Asia/Tokyo",
    "Australia/Sydney",
    ...supportedZones,
  ].filter(Boolean);

  return [
    { value: "", label: blankLabel, isSelected: !selected },
    ...[...new Set(zones)].map((zone) => ({
      value: zone,
      label: zone.replace(/_/g, " "),
      isSelected: zone === selected,
    })),
  ];
}

function normalizeEvent(event) {
  return {
    ...event,
    detailUrl: eventDetailUrl(event),
    displayDate: eventDate(event.starts_at),
    displayTimeRange: eventTimeRange(event.starts_at, event.ends_at, event.all_day),
    locationUrl: locationUrl(event.location_record),
    hasLocationUrl: Boolean(locationUrl(event.location_record)),
  };
}

function normalizeBusyTime(item) {
  return {
    ...item,
    detailUrl: item.event_id ? `/calendar/events/${item.event_id}` : "",
    displayTitle: item.private ? "Private reservation" : item.title,
    displayTimeRange: eventTimeRange(item.starts_at, item.ends_at),
  };
}

function normalizePendingReservation(item, actionId) {
  return {
    ...item,
    detailUrl: item.event_id ? `/calendar/events/${item.event_id}` : "",
    displayTitle: item.event_title || "Untitled event",
    displayTimeRange: eventTimeRange(item.starts_at, item.ends_at),
    requesterLabel: item.created_by_username || item.event_owner_username || "Unknown user",
    isBusy: actionId === item.id,
  };
}

export default class DiscourseEventsCalendarRoomController extends Controller {
  @service site;

  @tracked room = null;
  @tracked locations = [];
  @tracked loadError = "";
  @tracked isEditing = false;
  @tracked isSavingRoom = false;
  @tracked editError = "";
  @tracked editNotice = "";
  @tracked editName = "";
  @tracked editLocation = "";
  @tracked editLocationChanged = false;
  @tracked editSelectedLocationId = "";
  @tracked editImageUpload = null;
  @tracked editImageUploadId = "";
  @tracked editImageUploadError = "";
  @tracked isUploadingEditImage = false;
  @tracked editCapacity = "";
  @tracked editVisibility = "public_visibility";
  @tracked editPublicationStatus = "published";
  @tracked editBookingWindowDays = "";
  @tracked editBufferMinutes = "";
  @tracked editRequiresApproval = false;
  @tracked editDescription = "";
  @tracked editHoursTimezone = "";
  @tracked editHoursFields = buildHoursFields();
  @tracked editManagerGroupIds = [];
  @tracked editBookingGroupIds = [];
  @tracked reservationActionId = null;

  syncRoomFromModel(model) {
    this.room = model?.room || null;
    this.locations = model?.locations || [];
    this.loadError = model?.loadError || "";
    this.isEditing = false;
    this.isSavingRoom = false;
    this.editError = "";
    this.editNotice = "";
    this.resetEditFields();
  }

  get hasRoom() {
    return Boolean(this.room);
  }

  get locationUrl() {
    return locationUrl(this.room?.location_record);
  }

  get hasLocationUrl() {
    return Boolean(this.locationUrl);
  }

  get locationLabel() {
    return this.room?.location_record?.name || this.room?.location || "";
  }

  get locationAddressLabel() {
    const address = this.room?.location_record?.address;
    return address && address !== this.locationLabel ? address : "";
  }

  get editHoursTimezoneOptions() {
    return timezoneOptions(this.editHoursTimezone, "Inherit location/default");
  }

  get capacityLabel() {
    return this.room?.capacity ? `${this.room.capacity}` : "";
  }

  get hasRoomImage() {
    return Boolean(this.room?.image_url);
  }

  get editImagePreviewUrl() {
    return uploadPreviewUrl(this.editImageUpload);
  }

  get hasEditImageUpload() {
    return Boolean(this.editImagePreviewUrl);
  }

  get roomImageAlt() {
    return this.room?.name ? `Image for ${this.room.name}` : "Room image";
  }

  get canManageRoom() {
    return this.room?.can_manage === true;
  }

  get canManageRoomAccess() {
    return this.room?.can_manage_access === true;
  }

  get publicationLabel() {
    return resourcePublicationLabel(this.room?.publication_status);
  }

  get isDraft() {
    return this.room?.publication_status === "draft";
  }

  get hasLocations() {
    return this.locations.length > 0;
  }

  get groupOptions() {
    return (this.site?.groups || []).filter((group) => Number(group.id) > 0);
  }

  get groupsById() {
    return new Map(this.groupOptions.map((group) => [Number(group.id), group.name]));
  }

  get editLocationOptions() {
    return this.locations.map((location) => ({
      ...location,
      value: locationValue(location),
      isSelected: locationValue(location) === this.editSelectedLocationId,
    }));
  }

  get hasNoEditLocationSelected() {
    return !this.editSelectedLocationId;
  }

  findLocationForValue(value) {
    if (!value) {
      return null;
    }

    const lookupValue = `${value}`;

    return this.locations.find((location) =>
      [locationValue(location), location.slug, location.name, location.address]
        .filter(Boolean)
        .includes(lookupValue)
    );
  }

  findLocationForRoom() {
    return (
      this.findLocationForValue(this.room?.location_id) ||
      this.findLocationForValue(this.room?.location_record?.id) ||
      this.findLocationForValue(this.room?.location_record?.slug) ||
      this.findLocationForValue(this.room?.location)
    );
  }

  get isPublicVisibility() {
    return this.editVisibility === "public_visibility";
  }

  get isStaffVisibility() {
    return this.editVisibility === "staff_visibility";
  }

  get isPublishedPublicationStatus() {
    return this.editPublicationStatus === "published";
  }

  get isDraftPublicationStatus() {
    return this.editPublicationStatus === "draft";
  }

  get canSaveRoom() {
    return this.canManageRoom && this.editName.trim().length > 0 && !this.isSavingRoom;
  }

  get cannotSaveRoom() {
    return !this.canSaveRoom;
  }

  get policyItems() {
    const items = [];

    if (this.room?.booking_window_days) {
      items.push({
        label: "Booking window",
        value: `${this.room.booking_window_days} days`,
      });
    }

    if (this.room?.buffer_minutes) {
      items.push({
        label: "Buffer",
        value: `${this.room.buffer_minutes} minutes before and after reservations`,
      });
    }

    if (this.room?.requires_approval) {
      items.push({
        label: "Approval",
        value: "Reservations require manager approval",
      });
    }

    if (this.room?.hours_summary && this.room.hours_summary !== "No hours set") {
      items.push({
        label: "Hours",
        value: this.room.hours_summary,
      });
    }

    return items;
  }

  get hasPolicies() {
    return this.policyItems.length > 0;
  }

  get hasAccessPolicyData() {
    return Array.isArray(this.room?.manager_group_ids) || Array.isArray(this.room?.booking_group_ids);
  }

  get managerGroupsLabel() {
    return groupLabel(this.room?.manager_group_ids, this.groupsById);
  }

  get bookingGroupsLabel() {
    return groupLabel(this.room?.booking_group_ids, this.groupsById);
  }

  get managerAccessLabel() {
    if (!this.hasAccessPolicyData) {
      return "";
    }

    return this.managerGroupsLabel || "Staff only";
  }

  get bookingAccessLabel() {
    if (!this.hasAccessPolicyData) {
      return "";
    }

    return this.bookingGroupsLabel || "All eligible event creators";
  }

  get createEventUrl() {
    if (!this.room?.id) {
      return "/calendar?create=1";
    }

    const params = new URLSearchParams({ create: "1", room_id: `${this.room.id}` });
    if (this.room.location_id) {
      params.set("location_id", `${this.room.location_id}`);
    }

    return `/calendar?${params.toString()}`;
  }

  get upcomingEvents() {
    return sortEvents(this.room?.upcoming_events || []).map(normalizeEvent);
  }

  get hasUpcomingEvents() {
    return this.upcomingEvents.length > 0;
  }

  get pendingReservations() {
    return (this.room?.pending_reservations || []).map((reservation) =>
      normalizePendingReservation(reservation, this.reservationActionId)
    );
  }

  get hasPendingReservations() {
    return this.pendingReservations.length > 0;
  }

  get availabilityWindowLabel() {
    const availability = this.room?.availability;

    if (!availability?.starts_at || !availability?.ends_at) {
      return "";
    }

    const endDate = new Date(availability.ends_at);

    if (Number.isNaN(endDate.getTime())) {
      return "";
    }

    const lastVisibleDay = addDays(startOfDay(endDate), -1);

    return `${eventDate(availability.starts_at)} - ${fallbackDate(lastVisibleDay)}`;
  }

  get availabilityDays() {
    const availability = this.room?.availability;
    const busyTimes = availability?.busy_times || [];
    const startValue = availability?.starts_at ? new Date(availability.starts_at) : new Date();
    const firstDay = Number.isNaN(startValue.getTime()) ? startOfDay(new Date()) : startOfDay(startValue);

    return Array.from({ length: 14 }, (_, index) => {
      const date = addDays(firstDay, index);
      const nextDate = addDays(date, 1);
      const items = busyTimes.filter((item) => overlapsDay(item, date, nextDate)).map(normalizeBusyTime);
      const hours = effectiveHoursForDate(availability, date);

      return {
        key: date.toISOString(),
        weekday: fallbackWeekday(date),
        dateLabel: fallbackDate(date),
        statusLabel: hours.isBookable ? "Open" : "Closed",
        statusClass: hours.isBookable
          ? "discourse-events-calendar__availability-status discourse-events-calendar__availability-status--open"
          : "discourse-events-calendar__availability-status discourse-events-calendar__availability-status--closed",
        hoursLabel: hours.label,
        busyLabel: items.length === 1 ? "1 reservation" : items.length > 1 ? `${items.length} reservations` : "",
        isOpen: hours.isBookable,
        busyTimes: items,
      };
    });
  }

  get hasAvailabilityDays() {
    return this.availabilityDays.length > 0;
  }

  resetEditFields() {
    this.editName = this.room?.name || "";
    this.editLocation = this.room?.location || "";
    const selectedLocation = this.findLocationForRoom();
    this.editSelectedLocationId = selectedLocation ? locationValue(selectedLocation) : "";
    this.editLocation =
      this.room?.location || selectedLocation?.address || selectedLocation?.name || "";
    this.editLocationChanged = false;
    this.editImageUpload = uploadFromResource(this.room) || null;
    this.editImageUploadId = this.room?.image_upload_id ? `${this.room.image_upload_id}` : "";
    this.editImageUploadError = "";
    this.isUploadingEditImage = false;
    this.editCapacity = this.room?.capacity || "";
    this.editVisibility = this.room?.visibility || "public_visibility";
    this.editPublicationStatus = this.room?.publication_status || "published";
    this.editBookingWindowDays = this.room?.booking_window_days || "";
    this.editBufferMinutes = this.room?.buffer_minutes || "";
    this.editRequiresApproval = this.room?.requires_approval === true;
    this.editDescription = this.room?.description || "";
    this.editHoursTimezone = this.room?.hours_timezone || "";
    this.editHoursFields = buildHoursFields(this.room?.hours_of_operation || {});
    this.editManagerGroupIds = normalizeGroupIds(this.room?.manager_group_ids);
    this.editBookingGroupIds = normalizeGroupIds(this.room?.booking_group_ids);
  }

  @action
  startEditing() {
    this.resetEditFields();
    this.editError = "";
    this.editNotice = "";
    this.isEditing = true;
  }

  @action
  cancelEditing() {
    this.isEditing = false;
    this.editError = "";
    this.resetEditFields();
  }

  @action
  selectEditLocation(event) {
    this.editSelectedLocationId = event.target.value;
    const location = this.findLocationForValue(this.editSelectedLocationId);
    this.editLocation = location?.address || location?.name || "";
    this.editLocationChanged = true;
  }

  @action
  updateEditManagerGroups(groupIds) {
    this.editManagerGroupIds = normalizeGroupIds(groupIds);
  }

  @action
  updateEditBookingGroups(groupIds) {
    this.editBookingGroupIds = normalizeGroupIds(groupIds);
  }

  @action
  updateVisibility(event) {
    this.editVisibility = event.target.value;
  }

  @action
  updatePublicationStatus(event) {
    this.editPublicationStatus = event.target.value || "published";
  }

  @action
  updateRequiresApproval(event) {
    this.editRequiresApproval = event.target.checked;
  }

  @action
  updateDescription(event) {
    this.editDescription = event.target.value;
  }

  @action
  async uploadEditImage(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    this.editImageUploadError = "";
    this.isUploadingEditImage = true;

    try {
      const upload = await uploadImageFile(file);
      this.editImageUpload = upload;
      this.editImageUploadId = upload?.id ? `${upload.id}` : "";
    } catch (error) {
      this.editImageUploadError = extractErrorMessage(error);
      popupAjaxError(error);
    } finally {
      this.isUploadingEditImage = false;
    }
  }

  @action
  removeEditImage() {
    this.editImageUpload = null;
    this.editImageUploadId = "";
    this.editImageUploadError = "";
  }

  @action
  updateHoursTimezone(event) {
    this.editHoursTimezone = event.target.value;
  }

  @action
  toggleHoursDay(dayKey, event) {
    this.editHoursFields = updateHoursField(this.editHoursFields, dayKey, {
      enabled: event.target.checked,
    });
  }

  @action
  updateHoursStart(dayKey, event) {
    this.editHoursFields = updateHoursField(this.editHoursFields, dayKey, {
      start: event.target.value,
    });
  }

  @action
  updateHoursEnd(dayKey, event) {
    this.editHoursFields = updateHoursField(this.editHoursFields, dayKey, {
      end: event.target.value,
    });
  }

  @action
  async saveRoom(event) {
    event?.preventDefault();

    if (!this.canSaveRoom) {
      return;
    }

    this.isSavingRoom = true;
    this.editError = "";
    this.editNotice = "";

    const room = {
      name: this.editName,
      image_upload_id: this.editImageUploadId,
      capacity: this.editCapacity,
      booking_window_days: this.editBookingWindowDays,
      buffer_minutes: this.editBufferMinutes,
      requires_approval: this.editRequiresApproval,
      description: this.editDescription,
      hours_timezone: this.editHoursTimezone,
      hours_of_operation: serializeHoursFields(this.editHoursFields),
    };

    if (this.editSelectedLocationId) {
      room.location_id = this.editSelectedLocationId;
    } else if (this.editLocationChanged) {
      room.location_id = "";
      room.location = this.editLocation;
    } else {
      room.location = this.editLocation;
    }

    if (this.canManageRoomAccess) {
      room.visibility = this.editVisibility;
      room.publication_status = this.editPublicationStatus;
      room.manager_group_ids = this.editManagerGroupIds;
      room.booking_group_ids = this.editBookingGroupIds;
    }

    try {
      const response = await ajax(`/calendar/rooms/${this.room.id}.json`, {
        type: "PUT",
        data: { room },
      });

      this.room = response?.room || response || this.room;
      this.isEditing = false;
      this.editNotice = "Room updated.";
      this.resetEditFields();
    } catch (error) {
      this.editError = extractErrorMessage(error);
      popupAjaxError(error);
    } finally {
      this.isSavingRoom = false;
    }
  }

  async refreshRoom() {
    const response = await ajax(`/calendar/rooms/${this.room.id}.json`);
    this.room = response?.room || response || this.room;
    this.resetEditFields();
  }

  @action
  async approveReservation(reservation) {
    if (!reservation?.id || this.reservationActionId) {
      return;
    }

    this.reservationActionId = reservation.id;
    this.editError = "";
    this.editNotice = "";

    try {
      await ajax(`/calendar/reservations/${reservation.id}/approve.json`, { type: "POST" });
      await this.refreshRoom();
      this.editNotice = "Reservation approved.";
    } catch (error) {
      this.editError = extractErrorMessage(error);
      popupAjaxError(error);
    } finally {
      this.reservationActionId = null;
    }
  }

  @action
  async rejectReservation(reservation) {
    if (!reservation?.id || this.reservationActionId) {
      return;
    }

    const reason = window.prompt("Reason for rejection (optional)");
    if (reason === null) {
      return;
    }

    this.reservationActionId = reservation.id;
    this.editError = "";
    this.editNotice = "";

    try {
      await ajax(`/calendar/reservations/${reservation.id}/reject.json`, {
        type: "POST",
        data: { reason },
      });
      await this.refreshRoom();
      this.editNotice = "Reservation rejected.";
    } catch (error) {
      this.editError = extractErrorMessage(error);
      popupAjaxError(error);
    } finally {
      this.reservationActionId = null;
    }
  }
}
